import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../../components/Layout/Sidebar";
import SettingsService from "../../services/settingsService";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEdit, FiTrash2, FiPlusCircle } from "react-icons/fi";

export default function RevenueCategories() {
  const [formData, setFormData] = useState({ name: "" });
  const [revenue, setRevenue] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revenueToDelete, setRevenueToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRevenueCategories = async () => {
      try {
        const response = await SettingsService.getRevenueCategories();
        const revenueCategoryArray = Array.isArray(response.revenue)
          ? response.revenue
          : [];
        setRevenue(revenueCategoryArray);
      } catch (error) {
        console.error("Error fetching revenue categories:", error);
        setRevenue([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRevenueCategories();
  }, []);

  const handleView = (id) => navigate(`#`);
  const handleEdit = (id) => navigate(`#`);
  const handleDelete = (id) => {
    setRevenueToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (revenueToDelete) {
      try {
        setIsLoading(true);
        await SettingsService.deleteRevenue(revenueToDelete);
        setRevenue(revenue.filter((item) => item.id !== revenueToDelete));
      } catch (error) {
        console.error("Error deleting revenue category:", error);
      } finally {
        setIsLoading(false);
        setIsModalOpen(false);
        setRevenueToDelete(null);
      }
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage("");
  setError("");

  try {
    const data = await SettingsService.createRevenue(formData);
    // data = { status: true, message: "...", expense: {...} }

    if (data.revenue) {
      setRevenue((prev) => [...prev, data.revenue]); // add new row live
      setFormData({ name: "" });
      setMessage("Revenue registered successfully!");
    } else {
      setError("Invalid response from server");
    }
  } catch (err) {
    if (err.response && err.response.status === 422) {
      const validationErrors = err.response.data.errors;
      setError(
        validationErrors?.name
          ? validationErrors.name[0]
          : "Validation failed."
      );
    } else {
      setError("Something went wrong.");
    }
  }
};
  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 md:ml-64 mt-14 md:mt-0 px-4 py-4 md:px-6 md:py-6">
        {/* Page Title */}
        <div className="max-w-7xl mx-auto mb-6">
          <h1 className="text-3xl font-bold text-white bg-black p-4 rounded-md shadow text-center">
            Revenue Categories
          </h1>
        </div>

        {/* Form Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-xl mx-auto bg-white p-6 rounded-md shadow-lg"
        >
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <FiPlusCircle className="text-black" /> Register New Revenue
          </h2>

          {/* Feedback */}
          {message && (
            <p className="text-green-600 font-semibold mb-4">{message}</p>
          )}
          {error && <p className="text-red-600 font-semibold mb-4">{error}</p>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium">Name</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Enter expense category name..."
              />
            </div>

            <button
              type="submit"
              className="w-full bg-black text-white px-6 py-2 rounded-md hover:bg-gray-900 flex items-center justify-center gap-2"
            >
              <FiPlusCircle /> Create Revenue
            </button>
          </form>
        </motion.div>

        {/* List Section */}
        <div className="max-w-7xl mx-auto bg-white p-6 rounded-md shadow-lg mt-6 overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-black"></div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-4">All Registered Revenues</h2>
              <table className="min-w-full table-auto border border-gray-200 rounded-md overflow-hidden">
                <thead>
                  <tr className="bg-gray-200 text-left">
                    <th className="px-4 py-2 border">#</th>
                    <th className="px-4 py-2 border">Name</th>
                    <th className="px-4 py-2 border text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="text-center py-4 text-gray-500"
                      >
                        No revenue found.
                      </td>
                    </tr>
                  ) : (
                    revenue.map((item, index) => (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-2 border text-center">
                          {index + 1}
                        </td>
                        <td className="px-4 py-2 border">{item.name}</td>
                      <td className="px-1 py-2 md:px-2 md:py-2 border text-center">
                        <div className="flex flex-col sm:flex-row sm:space-x-2 justify-center">
                          <button
                            onClick={() => handleView(item.id)}
                            className="w-24 px-1 py-1 bg-black text-white rounded hover:bg-blue-600 m-1 sm:mb-0 text-sm text-center"
                          >
                            <FiEye className="inline mr-1" /> View
                          </button>
                          <button
                            onClick={() => handleEdit(item.id)}
                            className="w-24 px-2 py-1 bg-black text-white rounded hover:bg-blue-600 m-1 sm:mb-0 text-sm text-center"
                          >
                            <FiEdit className="inline mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="w-24 px-2 py-1 bg-black text-white rounded hover:bg-blue-600 m-1 sm:mb-0 text-sm text-center"
                          >
                            <FiTrash2 className="inline mr-1" /> Delete
                          </button>
                        </div>
                      </td>

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0, y: 500 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 500 }}
                className="bg-white p-6 rounded-md shadow-lg max-w-sm w-full"
              >
                <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                <p className="mb-6 text-gray-700">
                  Are you sure you want to delete this revenue? This action
                  cannot be undone.
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setRevenueToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-black rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
